import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import AppRouter from './routes/AppRouter';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import * as Sentry from '@sentry/react';
import { SnackbarProvider } from 'notistack';

// Cloudflare Dashboard Tema - Birebir Kopya
const theme = createTheme({
  palette: {
    primary: { 
      main: '#f38020', // Cloudflare turuncu
      light: '#ff9f4a', 
      dark: '#e06a00', 
      contrastText: '#ffffff' 
    },
    secondary: { 
      main: '#2c7cb0', // Cloudflare mavi
      light: '#4a9bd4', 
      dark: '#1a5f8a', 
      contrastText: '#ffffff' 
    },
    error: { main: '#e53e3e' }, 
    warning: { main: '#dd6b20' }, 
    info: { main: '#3182ce' }, 
    success: { main: '#38a169' },
    background: { 
      default: '#f7f9fa', // Cloudflare arka plan
      paper: '#ffffff' 
    },
    text: { 
      primary: '#1a202c', // Cloudflare ana metin
      secondary: '#4a5568' // Cloudflare ikincil metin
    },
    grey: {
      50: '#f7f9fa',
      100: '#edf2f7',
      200: '#e2e8f0',
      300: '#cbd5e0',
      400: '#a0aec0',
      500: '#718096',
      600: '#4a5568',
      700: '#2d3748',
      800: '#1a202c',
      900: '#171923',
    }
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: { 
      fontWeight: 700, 
      fontSize: '2.25rem', 
      lineHeight: 1.2,
      color: '#1a202c'
    },
    h2: { 
      fontWeight: 600, 
      fontSize: '1.875rem', 
      lineHeight: 1.3,
      color: '#1a202c'
    },
    h3: { 
      fontWeight: 600, 
      fontSize: '1.5rem', 
      lineHeight: 1.4,
      color: '#1a202c'
    },
    h4: { 
      fontWeight: 600, 
      fontSize: '1.25rem', 
      lineHeight: 1.4,
      color: '#1a202c'
    },
    h5: { 
      fontWeight: 600, 
      fontSize: '1.125rem', 
      lineHeight: 1.4,
      color: '#1a202c'
    },
    h6: { 
      fontWeight: 600, 
      fontSize: '1rem', 
      lineHeight: 1.4,
      color: '#1a202c'
    },
    body1: { 
      fontSize: '0.875rem', 
      lineHeight: 1.5,
      color: '#4a5568'
    },
    body2: { 
      fontSize: '0.8125rem', 
      lineHeight: 1.5,
      color: '#4a5568'
    },
    button: { 
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.875rem'
    },
    caption: {
      fontSize: '0.75rem',
      color: '#718096'
    }
  },
  components: {
    MuiPaper: { 
      styleOverrides: { 
        root: { 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0'
        } 
      } 
    },
    MuiButton: { 
      styleOverrides: { 
        root: { 
          borderRadius: '6px',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '8px 16px',
          textTransform: 'none'
        },
        contained: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)'
          }
        }
      } 
    },
    MuiTextField: { 
      defaultProps: { 
        variant: 'outlined' 
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            fontSize: '0.875rem'
          }
        }
      }
    },
    MuiSelect: { 
      defaultProps: { 
        variant: 'outlined' 
      },
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontSize: '0.875rem'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: 500
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0'
        }
      }
    }
  },
  shape: {
    borderRadius: 8
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
    '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    '0 25px 50px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  ]
});

function App() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const appEl = (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider 
        maxSnack={3}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={4000}
      >
        <AuthProvider>
          <GoogleMapsProvider>
            <Router>
              <AppRouter />
            </Router>
          </GoogleMapsProvider>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
  if (dsn) {
    Sentry.init({ dsn, tracesSampleRate: 0.2, environment: process.env.NODE_ENV || 'development' });
    return <Sentry.ErrorBoundary fallback={<div>Beklenmeyen bir hata oluştu.</div>}>{appEl}</Sentry.ErrorBoundary>;
  }
  return appEl;
}

export default App;
